import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskLine } from "@/components/Reveal";
import { CONTACT, HERO_IMAGE } from "@/data/content";

const fadeUp = (delay) => ({
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.9, delay, ease: "easeInOut" },
});

const HeroCopy = ({ y }) => (
    <motion.div style={{ y }} className="lg:col-span-7">
        <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="mb-8 flex items-center gap-4 text-xs uppercase tracking-[0.3em] text-timber"
        >
            <span className="h-px w-10 bg-terra/60" />
            Interior Design Studio — Kolkata
        </motion.p>
        <h1 data-testid="hero-heading" className="font-serif text-4xl font-light leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-6xl">
            <MaskLine delay={0.2}>Conceived Together.</MaskLine>
            <MaskLine delay={0.38}>
                <em className="font-normal text-terra">Engineered Forever.</em>
            </MaskLine>
        </h1>
        <motion.p data-testid="hero-subtext" {...fadeUp(0.7)} className="mt-8 max-w-xl text-base font-light leading-relaxed text-mute sm:text-lg">
            Your home is not a template—it is a reflection of who you are. We begin with
            you—your habits, your preferences, your way of living—and shape spaces that are
            visually refined, intuitively yours, and built to endure.
        </motion.p>
        <HeroActions />
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.15 }}
            className="mt-14 flex flex-wrap gap-x-10 gap-y-3 font-mono text-[11px] uppercase tracking-[0.2em] text-fog"
        >
            <span>10+ Years of Practice</span>
            <span>Architects & Engineers</span>
            <span>Residential · Commercial</span>
        </motion.div>
    </motion.div>
);

const HeroActions = () => (
    <motion.div {...fadeUp(0.9)} className="mt-10 flex flex-wrap items-center gap-6">
        <a
            data-testid="hero-cta-whatsapp"
            href={CONTACT.whatsappHref}
            target="_blank"
            rel="noreferrer"
            className="group inline-flex items-center gap-3 rounded-full bg-ink px-7 py-3.5 text-xs uppercase tracking-[0.2em] text-ivory transition-colors duration-300 hover:bg-terra"
        >
            Begin Your Journey
            <span className="h-1.5 w-1.5 rotate-45 bg-gold transition-transform duration-300 group-hover:rotate-[135deg]" />
        </a>
        <a href={CONTACT.phoneHref} className="group relative text-xs uppercase tracking-[0.2em] text-wood">
            {CONTACT.phoneDisplay}
            <span className="absolute -bottom-1 left-0 h-px w-full bg-wood/30 transition-colors duration-300 group-hover:bg-terra" />
        </a>
    </motion.div>
);

const HeroImage = ({ y }) => (
    <div className="lg:col-span-5">
        <motion.div style={{ y }} className="relative ml-auto max-w-md lg:max-w-none">
            <div className="absolute -inset-0 translate-x-4 translate-y-4 border border-gold/40" />
            <motion.div
                initial={{ clipPath: "inset(100% 0 0 0)" }}
                animate={{ clipPath: "inset(0% 0 0 0)" }}
                transition={{ duration: 1.3, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="relative overflow-hidden"
            >
                <motion.img
                    src={HERO_IMAGE.src}
                    alt={HERO_IMAGE.alt}
                    initial={{ scale: 1.2 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 1.6, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    className="aspect-[4/5] w-full object-cover"
                />
            </motion.div>
            <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.4, duration: 0.8 }}
                className="mt-4 text-right font-mono text-[10px] uppercase tracking-[0.25em] text-fog"
            >
                {HERO_IMAGE.caption}
            </motion.p>
        </motion.div>
    </div>
);

const ScrollCue = () => (
    <motion.a
        href="#intro"
        aria-label="Scroll to introduction"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-6 flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-fog transition-colors hover:text-ink sm:left-12 lg:left-20"
    >
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}>
            <ArrowDown size={13} strokeWidth={1.5} />
        </motion.span>
        Scroll
    </motion.a>
);

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
    const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);

    return (
        <section id="top" ref={ref} data-testid="manifesto-chapter-01" className="relative flex min-h-screen items-center overflow-hidden pb-20 pt-28 lg:pt-32">
            <span aria-hidden className="pointer-events-none absolute -right-10 top-24 select-none font-serif text-[14rem] italic leading-none text-wood/[0.04] lg:text-[24rem]">
                N
            </span>
            <div className="relative mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-14 px-6 sm:px-12 lg:grid-cols-12 lg:gap-10 lg:px-20">
                <HeroCopy y={textY} />
                <HeroImage y={imgY} />
            </div>
            <ScrollCue />
        </section>
    );
};
